/**
 * ColumnHeaderSlot - Renders controls in the column-header location.
 *
 * This slot appears above each column and is typically used
 * to show column-specific controls in compare mode (e.g., run selector, step label).
 *
 * Two distinct rendering modes:
 * 1. FlexboxColumnHeader (fullscreen): Complex priority-based flexbox layout
 * 2. StaticControlRow (non-fullscreen): Static, comma-separated text in a single line
 */

import {type MediaCardType} from '@wandb/weave/common/types/media';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../../Component';
import {type StepMeta} from '../../types';
import {ControlRenderer} from '../renderers/ControlRenderer';
import {type ControlDefinition, type GridCell, type GridData} from '../types';
import {createControlGroups, getControlStyles} from '../utils/controlLayout';
import {StaticControlRow} from './StaticControlRow';

export type ColumnHeaderSlotProps = {
  controls: ControlDefinition[];
  columnIndex: number;
  cell?: GridCell;
  disableRunLinks?: boolean;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  mediaSliderKey: string;
  mediaType: MediaCardType;
  isMaxStepLoading: boolean;
  mediaBrowserSize: {width: number; height: number};
  gridData: GridData;
  isFullScreenPanelPage?: boolean;
};

/**
 * Renders controls in the column-header location using flexbox.
 *
 * Layout behavior:
 * - Container uses flex-wrap to allow controls to wrap onto multiple lines
 * - Each control group has flexBasis (initial size) and flexGrow (growth ratio)
 * - flexShrink: 1 allows controls to shrink below basis when constrained
 * - minWidth: 0 allows flex-shrink to work properly (overrides default min-content)
 * - Child controls use w-full to fill their parent container
 * - Extra space distributed proportionally: run(9), mediaKey(7), stepIndex(6)
 * - Step and index stay together in a wrapper (treated as single flex item)
 *
 * @param props - Column header slot props
 * @returns Rendered column header or null if hidden/empty
 */
/**
 * Renders controls in the column-header location with two distinct layouts:
 * 1. Fullscreen: Complex flexbox layout with priority-based growth/shrink
 * 2. Non-fullscreen: Static text row with comma-separated controls for compact display
 */
export const ColumnHeaderSlot: React.FC<ColumnHeaderSlotProps> = props => {
  if (props.controls.length === 0) {
    return null;
  }

  // Split into two completely different rendering paths for clarity
  if (props.isFullScreenPanelPage) {
    return <FlexboxColumnHeader {...props} />;
  } else {
    return (
      <StaticControlRow
        {...props}
        isFullScreenPanelPage={false}
        keyPrefix="header"
      />
    );
  }
};

/**
 * Complex flexbox layout for fullscreen mode.
 * Uses priority-based growth/shrink with responsive wrapping.
 * - Run: min 180px, grows with priority 9
 * - MediaKey: min 140px, grows with priority 7
 * - Step+Index: min 120px combined, grows with priority 6
 */
const FlexboxColumnHeader: React.FC<ColumnHeaderSlotProps> = ({
  controls,
  columnIndex,
  disableRunLinks,
  config,
  updateConfig,
  stepMeta,
  isMaxStepLoading,
  mediaBrowserSize,
  gridData,
  isFullScreenPanelPage,
}) => {
  const controlGroups = createControlGroups(controls);
  const isCompareMode = config.mode === 'compare';
  const gapClass = isCompareMode ? 'gap-4' : 'gap-8';

  const containerClasses = twMerge(
    'w-full', // Allow full expansion in fullscreen
    'flex flex-1 flex-wrap',
    'max-w-full',
    'items-start justify-center',
    'text-wb-text-primary',
    gapClass
  );

  return (
    <div className={containerClasses}>
      {controlGroups.map((group, groupIdx) => {
        const groupStyles = getControlStyles(group.name);
        const isStepIndexGroup = group.name === 'stepIndex';

        const groupClasses = twMerge(
          `footer-${group.name}-${groupIdx}`,
          'flex flex-row flex-nowrap',
          'items-center justify-center',
          gapClass,
          // Priority-based flexbox layout
          groupStyles.flexGrow,
          groupStyles.minWidth,
          'basis-0 shrink-0',
          // Special handling for step+index group
          isStepIndexGroup && 'shrink-1 h-48'
        );

        return (
          <div
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={`${group.name}-${groupIdx}-${columnIndex}`}
            className={groupClasses}>
            {group.controls.map(control => (
              <div
                key={`group-${control!.control}-${columnIndex}`}
                className="flex flex-shrink-1 w-full min-w-0">
                <ControlRenderer
                  control={control!}
                  index={controls.indexOf(control!)}
                  config={config}
                  stepMeta={stepMeta}
                  columnIndex={columnIndex}
                  disableRunLinks={disableRunLinks}
                  updateConfig={updateConfig}
                  isMaxStepLoading={isMaxStepLoading}
                  mediaBrowserSize={mediaBrowserSize}
                  gridData={gridData}
                  isFullScreenPanelPage={isFullScreenPanelPage}
                  layout="vertical"
                />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};
